<template>
  <div ref="container" :class="playStationPartnershipClasses">
    <ClientOnly>
      <PartnershipBanner v-if="partnerId" :partner="partnerId" />
    </ClientOnly>

    <div
      v-if="isLoadingScreen"
      class="flex min-h-screen flex-col items-center justify-center"
    >
      <RevLoadingScreen class="my-auto" :text="i18n(translations.loading)" />
    </div>

    <div v-if="!isLoadingScreen" class="flex min-h-[calc(100vh-3.75rem)]">
      <div
        class="bg-surface-default-hi hidden lg:block lg:w-[33%] lg:px-96 lg:pt-32"
      >
        <RevLink
          class="mb-24 flex items-center"
          data-test="back-link"
          @click="goBack"
        >
          <IconArrowLeft size="small" />
          <span class="ml-12">
            {{ backLink.label }}
          </span>
        </RevLink>

        <DeviceSummary
          v-if="displaySummary"
          data-test="device-summary"
          :summary
        />
      </div>
      <div class="grow scroll-mt-96 p-16 lg:w-auto lg:px-72 lg:py-32">
        <template v-if="displayFunnel">
          <div class="lg:m-auto lg:w-[39.5rem]">
            <RevLink
              v-if="stepperHasBackButton"
              class="mb-24 flex items-center lg:hidden"
              data-test="back-link"
              @click="goBack"
            >
              <IconArrowLeft size="small" />
              <span class="ml-12">
                {{ backLink.label }}
              </span>
            </RevLink>

            <QuestionsForm
              :active-step
              :auto-next="showFunnelButton ? false : true"
              class=""
              :funnel
              :has-submit-button="false"
              :is-loading-offer
              @next-question="getNextQuestion"
              @next-step="getNextStep"
              @submit-answers="getOffer"
            />

            <div
              v-if="showFunnelButton"
              class="flex pt-24 lg:m-auto lg:w-[39.5rem] lg:justify-end"
            >
              <RevButton
                :form="QUESTIONS_FORM_ID"
                full-width="adaptive"
                type="submit"
                variant="primary"
              >
                {{ i18n(translations.continue) }}
              </RevButton>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { navigateTo, useAsyncData, useRoute } from '#imports'
import { computed, inject, ref, watch, watchEffect } from 'vue'
import type { LocationQuery } from 'vue-router'

import type { Step } from '@backmarket/http-api/src/api-specs-buyback/customer/getQuestionsV3'
import { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevLink } from '@ds/components/Link'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { IconArrowLeft } from '@ds/icons/IconArrowLeft'

import { useBuybackFunnelOffer } from '~/scopes/buyback/composables/useBuybackFunnelOffer'
import { FUNNEL_ERROR_MESSAGE } from '~/scopes/buyback/pages/constants'

import DeviceSummary from '../components/DeviceSummary/DeviceSummary.vue'
import PartnershipBanner from '../components/PartnershipBanner/PartnershipBanner.vue'
import QuestionsForm, {
  type NextQuestionPayload,
  type NextStepPayload,
  type SubmitAnswersPayload,
} from '../components/QuestionsForm/QuestionsForm.vue'
import { QUESTIONS_FORM_ID } from '../components/QuestionsForm/constants'
import type { ErrorData } from '../components/TheCatcher/useCatcher'
import { ROUTE_NAMES } from '../constants'

import translations from './DeviceAssessment.translations'

const i18n = useI18n()
const route = useRoute()
const logger = useLogger()
const breakpoint = useClientOnlyComputedBreakpoint()
const tracking = useTracking()
const { openErrorToast } = useTheToast()
const { setBuyBackOffer } = useBuybackFunnelOffer()
const $httpFetch = useHttpFetch()

const showFunnelButton = ref(false)
const scrollToTop = ref(false)
const isLoadingScreen = ref(true)
const isLoadingOffer = ref(false)
const errorData = inject<ErrorData>('errorData', ref({ message: '', step: '' }))
errorData.value = {
  message: FUNNEL_ERROR_MESSAGE.BUYBACK_FUNNEL,
  step: 'DeviceAssessment',
}

const { params } = route

const container = ref<HTMLElement | null>(null)

const queryParamsPayload = computed(() => ({
  ...route.query,
  category: params.id,
  embedded: 'true',
  // Needed because the start link from trade-in HP has a nextStep=false query param
  ...insertIf(showFunnelButton.value === false, {
    nextStep: 'true',
  }),
}))

const {
  data: buybackQuestions,
  status: buybackQuestionsStatus,
  error: buybackQuestionsError,
} = useAsyncData(
  () =>
    $httpFetch('/buyback-funnel/api/v1/funnel/:kind/questions', {
      query: queryParamsPayload.value,
      pathParams: { kind: 'regular' },
    }),
  { server: false, watch: [queryParamsPayload] },
)

watchEffect(() => {
  if (buybackQuestionsStatus.value === 'success') {
    isLoadingScreen.value = false
  }

  if (buybackQuestionsStatus.value === 'success' && scrollToTop.value) {
    container.value?.scrollIntoView({ block: 'start' })
    scrollToTop.value = false
  }

  // Check if response from API is 200 but empty
  // Had happened during caos testing in the past
  if (
    !['pending', 'idle'].includes(buybackQuestionsStatus.value) &&
    !buybackQuestionsError.value &&
    isEmpty(buybackQuestions.value)
  ) {
    logger.error(FUNNEL_ERROR_MESSAGE.EMPTY_QUESTIONS, {
      error: new Error('Empty response from questions API'),
      info: queryParamsPayload.value,
      owners: ['bot-squad-circularity-offer-front'],
    })
  }
})

watch(buybackQuestionsError, () => {
  if (!buybackQuestionsError.value) return

  logger.error(FUNNEL_ERROR_MESSAGE.GET_QUESTIONS, {
    statusCode: buybackQuestionsError.value.statusCode,
    statusMessage: buybackQuestionsError.value.message,
    info: queryParamsPayload.value,
    owners: ['bot-squad-circularity-offer-front'],
  })

  openErrorToast({ content: i18n(translations.refreshPage) })
})

const funnel = computed(() => buybackQuestions.value?.funnel || [])
const summary = computed(() => buybackQuestions.value?.summary || [])

const partnerId = computed(() => {
  const { partner } = route.query

  return partner && typeof partner === 'string' && partner.toLowerCase()
})

const playStationPartnershipClasses = computed(() => {
  // this will make sure scrollIntoView take into account header and banner
  return partnerId.value ? 'scroll-mt-[200px]' : ''
})

const displaySummary = computed(() => {
  return !isEmpty(summary.value)
})

const backLink = computed(() => {
  const previousStepIndex =
    funnel.value.findIndex(({ step }) => step?.active) - 1

  // first step -> back to trade-in HP
  if (previousStepIndex < 0) {
    return {
      label: i18n(translations.goBack),
      to: {
        name: 'buyback',
        params: {
          pageName: 'home',
        },
      },
    }
  }

  const routeQuery: LocationQuery = {
    ...route.query,
    nextStep: 'false',
  }
  // Get last question(s)/answer(s)
  const { questions = [] } =
    (funnel.value && funnel.value.find(({ step }) => step?.active)) || {}
  // Delete last question(s)/answer(s) from route query
  questions.forEach(({ key }) => {
    delete routeQuery[key]
  })

  return {
    label: i18n(translations.goBack),
    to: {
      ...route,
      query: routeQuery,
    },
  }
})

const activeStep = computed(() => {
  const activeFunnelStep = funnel.value.find(({ step }) => step?.active)

  return activeFunnelStep?.step || null
})

const stepperHasBackButton = computed(() => {
  return breakpoint.value <= 2
})

const displayFunnel = computed(() => {
  return !isEmpty(funnel.value)
})

const goBack = async () => {
  showFunnelButton.value = true
  await navigateTo(backLink.value.to, { replace: true })
}

const getNextQuestion = async ({
  formPayload = {},
  reset = [],
  nextStep = null,
}: NextQuestionPayload) => {
  const formGlobal = { ...route.query, ...formPayload }

  if (!isEmpty(reset)) {
    reset.forEach((key) => {
      delete formGlobal[key as keyof typeof formGlobal]
    })
  }

  await navigateTo({
    ...route,
    query: {
      ...formGlobal,
      nextStep: nextStep || (showFunnelButton.value ? 'false' : 'true'),
    },
  })
}

const formatTrackingPayload = (payload: Record<string, string | string[]>) => {
  const formattedPayload = {
    ...payload,
  }
  delete formattedPayload.nextStep

  return formattedPayload
}

const getNextStep = ({ formPayload, shouldScrollToTop }: NextStepPayload) => {
  showFunnelButton.value = false

  getNextQuestion({
    formPayload,
    reset: [],
    nextStep: 'true',
  })

  scrollToTop.value = shouldScrollToTop
}

const getOffer = async ({ formPayload }: SubmitAnswersPayload) => {
  isLoadingScreen.value = true
  const queryParams = {
    category: params.id,
    ...route.query,
    ...formPayload,
  }

  const { model: name, ...restPayload } = formatTrackingPayload(queryParams)
  tracking.trackBuybackDeviceSpecifications({
    product: {
      ...restPayload,
      name,
    },
  })

  try {
    const getOfferV1Payload = await $httpFetch(
      '/buyback-funnel/api/v1/funnel/:kind/offer',
      {
        query: queryParams,
        pathParams: { kind: 'regular' },
      },
    )

    setBuyBackOffer(getOfferV1Payload, queryParams)

    const { listingPublicId } = getOfferV1Payload.listing

    tracking.trackClick({
      zone: 'buyback',
      name: 'see_estimate',
      value: {
        ...formatTrackingPayload(queryParams),
        listing_public_id: listingPublicId,
      },
    })

    await navigateTo({
      name: ROUTE_NAMES.OFFER,
      params: {
        ...route.params,
        listingId: listingPublicId,
      },
      query: {
        partner: route.query.partner,
      },
    })
  } catch (error) {
    const httpError = error as HttpApiError

    tracking.trackClick({
      zone: 'buyback',
      name: 'see_estimate',
      value: {
        ...formatTrackingPayload(queryParams),
        listing_public_id: 'no_offer',
      },
    })

    if (httpError.status === 404) {
      tracking.trackBuybackNoOffer({
        value: formatTrackingPayload(queryParams),
      })

      await navigateTo({
        name: ROUTE_NAMES.NO_OFFER,
        params: route.params,
      })
    } else {
      logger.error(FUNNEL_ERROR_MESSAGE.GET_OFFER, {
        error: httpError,
        owners: ['bot-squad-circularity-offer-front'],
      })
      openErrorToast()
    }
  } finally {
    isLoadingScreen.value = false
  }
}

function handleTracking(currentStep: Step) {
  tracking.trackClick({
    zone: 'buyback',
    name: currentStep.key,
    value: formatTrackingPayload({
      category: route.params.id,
      ...route.query,
    }),
  })
}

watch(queryParamsPayload, () => {
  if (activeStep.value) {
    handleTracking(activeStep.value)
  }
})
</script>
